import React, { useEffect, useRef, useState } from "react";

/**
 * Lightweight, performant sparkle effect for the hero/top of the landing page.
 *
 * Design rules (kept the site fast):
 *   • Pure CSS keyframe animations — no JS-per-frame loops.
 *   • Only GPU-friendly properties animated (transform + opacity).
 *   • `pointer-events: none` so it never blocks the UI.
 *   • Particles pause via `animation-play-state: paused` when off-screen.
 *   • Fades in once the user starts scrolling, fades out past the hero zone.
 *   • Respects `prefers-reduced-motion`.
 *   • Just 14 particles → negligible CPU.
 */

const SPARKLE_COUNT = 22;

// SVG sparkle — elongated 4-pointed "twinkle" shape matching the logo.
// viewBox is 24×72 (1:3) so the shape stays naturally taller than wide.
const SparkleSymbol = () => (
  <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
    <defs>
      <symbol id="st-sparkle" viewBox="0 0 24 72">
        <path
          d="M12 0 C12 28 14 34 24 36 C14 38 12 44 12 72 C12 44 10 38 0 36 C10 34 12 28 12 0 Z"
          fill="currentColor"
        />
      </symbol>
    </defs>
  </svg>
);

// Pre-computed deterministic particle data so SSR/pre-rendering stays stable
const PARTICLES = Array.from({ length: SPARKLE_COUNT }).map((_, i) => {
  // simple LCG so output is deterministic
  const rand = (seed) => {
    const x = Math.sin(seed * 9301 + 49297) * 233280;
    return x - Math.floor(x);
  };
  const left = rand(i + 1) * 100; // %
  const size = 12 + rand(i + 2) * 24; // 12–36 px (bigger, more visible)
  const delay = -rand(i + 3) * 18; // negative — pre-populates the scene so some are already mid-fall
  const duration = 14 + rand(i + 4) * 10; // 14–24s
  const drift = (rand(i + 5) - 0.5) * 100; // -50px to +50px lateral drift
  const opacity = 0.7 + rand(i + 6) * 0.3; // 0.7–1.0 (stronger)
  const isPeach = rand(i + 7) > 0.4; // mix of peach + white
  return { left, size, delay, duration, drift, opacity, isPeach };
});

const Sparkles = () => {
  const [visible, setVisible] = useState(false);
  const [inView, setInView] = useState(true);
  const wrapRef = useRef(null);

  // Fade in once user starts scrolling; fade out past the hero zone.
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY || window.pageYOffset || 0;
        const vh = window.innerHeight;
        // Visible from 60px down to ~1.5 screens (the hero area)
        setVisible(y > 60 && y < vh * 1.5);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Pause animations entirely when the element is off-screen
  useEffect(() => {
    if (!wrapRef.current || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  // Honour reduced-motion preference
  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reducedMotion) return null;

  return (
    <>
      <SparkleSymbol />
      <div
        ref={wrapRef}
        aria-hidden="true"
        data-testid="hero-sparkles"
        className="pointer-events-none fixed inset-x-0 top-0 h-screen overflow-hidden z-[35] transition-opacity duration-700"
        style={{
          opacity: visible ? 1 : 0,
        }}
      >
        {PARTICLES.map((p, i) => (
          <svg
            key={i}
            width={p.size * 0.42}
            height={p.size * 1.25}
            className="absolute will-change-transform"
            style={{
              top: -40,
              left: `${p.left}%`,
              color: p.isPeach ? "#daae9c" : "#ffffff",
              opacity: 0,
              animation: `st-sparkle-fall ${p.duration}s linear ${p.delay}s infinite, st-sparkle-twinkle ${
                3 + (i % 4)
              }s ease-in-out ${p.delay}s infinite`,
              // Always running while in view — the container's opacity fades them
              // in/out smoothly so they appear immediately on scroll.
              animationPlayState: inView ? "running" : "paused",
              filter:
                "drop-shadow(0 0 8px rgba(218, 174, 156, 0.55)) drop-shadow(0 0 2px rgba(255, 255, 255, 0.65))",
              ["--drift"]: `${p.drift}px`,
              ["--peak-opacity"]: p.opacity,
            }}
          >
            <use href="#st-sparkle" />
          </svg>
        ))}
      </div>

      <style>{`
        @keyframes st-sparkle-fall {
          0% {
            transform: translate3d(0, 0, 0);
          }
          100% {
            transform: translate3d(var(--drift), 95vh, 0);
          }
        }
        @keyframes st-sparkle-twinkle {
          0%, 100% { opacity: 0; }
          15% { opacity: var(--peak-opacity); }
          50% { opacity: calc(var(--peak-opacity) * 0.5); }
          85% { opacity: var(--peak-opacity); }
        }
      `}</style>
    </>
  );
};

export default Sparkles;
